import { useList, useDelete, useGo } from '@refinedev/core';
import { useMemo } from 'react';
import { notify } from '@/lib/notify';
import { Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTable,
  TableToolbar,
  FilterSelect,
  type Column,
  type SortState,
  type BulkAction,
  type FilterOption,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { useConfirm } from '@/hooks/use-confirm';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { LegalPage } from './types';

function getRuTitle(page: LegalPage): string {
  const translations = page.translations ?? [];
  const ru = translations.find((t) => t.languageCode === 'ru');
  return ru?.title ?? translations[0]?.title ?? page.slug;
}

type PublishedFilter = 'all' | 'true' | 'false';

const publishedFilterOptions: FilterOption[] = [
  { label: 'Все статусы', value: 'all' },
  { label: 'Опубликовано', value: 'true' },
  { label: 'Черновик', value: 'false' },
];

export function LegalPagesList() {
  useScrollRestoration();
  const go = useGo();
  const [page, setPage] = usePersistedState('legal-pages:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('legal-pages:sort', { field: 'updatedAt', order: 'desc' });
  const [search, setSearch] = usePersistedState('legal-pages:search', '');
  const [publishedFilter, setPublishedFilter] = usePersistedState<PublishedFilter>('legal-pages:publishedFilter', 'all');
  const pageSize = 20;

  const listResult = useList<LegalPage>({
    resource: 'legal-pages',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: useMemo(() => {
      const f: { field: string; operator: 'contains' | 'eq'; value: string }[] = [];
      if (search) {
        f.push({ field: 'search', operator: 'contains', value: search });
      }
      if (publishedFilter !== 'all') {
        f.push({ field: 'isPublished', operator: 'eq', value: publishedFilter });
      }
      return f;
    }, [search, publishedFilter]),
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const { mutate: deleteMutation } = useDelete();
  const { confirm, ConfirmationDialog } = useConfirm();

  const handleDelete = async (items: LegalPage[]) => {
    if (items.length === 0) { return; }

    const confirmed = await confirm({
      title:
        items.length === 1
          ? `Удалить страницу «${getRuTitle(items[0])}»?`
          : `Удалить ${items.length} страниц?`,
      description: 'Это действие необратимо.',
      variant: 'destructive',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });

    if (!confirmed) { return; }

    for (const item of items) {
      deleteMutation(
        { resource: 'legal-pages', id: item.id },
        {
          onSuccess: () => {
            void listResult.query.refetch();
          },
          onError: (error) => {
            const message = error instanceof Error ? error.message : 'Не удалось удалить страницу';
            notify.error(message);
          },
        },
      );
    }
  };

  const columns: Column<LegalPage>[] = [
    {
      key: 'slug',
      header: 'Slug',
      sortable: true,
      render: (_value, record) => (
        <span className="font-mono text-sm">{record.slug}</span>
      ),
    },
    {
      key: 'title',
      header: 'Заголовок',
      render: (_value, record) => getRuTitle(record),
    },
    {
      key: 'isPublished',
      header: 'Статус',
      sortable: true,
      render: (_value, record) => (
        <Badge variant={record.isPublished ? 'default' : 'secondary'}>
          {record.isPublished ? 'Опубликовано' : 'Черновик'}
        </Badge>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Обновлено',
      sortable: true,
      render: (_value, record) => new Date(record.updatedAt).toLocaleDateString('ru-RU'),
    },
  ];

  const bulkActions: BulkAction<LegalPage>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/legal-pages/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Редактировать',
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/legal-pages/edit/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      onClick: (items) => { void handleDelete(items); },
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Юридические страницы"
        sticky
        actions={
          <Button onClick={() => go({ to: '/legal-pages/create' })} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Создать
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <TableToolbar
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            searchPlaceholder="Поиск по slug или заголовку..."
          />
        </div>
        <FilterSelect
          label="Статус публикации"
          value={publishedFilter}
          options={publishedFilterOptions.filter((o) => o.value !== 'all')}
          allLabel="Все статусы"
          onChange={(value) => {
            setPublishedFilter(value as PublishedFilter);
            setPage(1);
          }}
          className="w-[200px]"
        />
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(record) => go({ to: `/legal-pages/edit/${record.id}` })}
        emptyMessage="Нет юридических страниц"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {ConfirmationDialog}
    </div>
  );
}
